import { apiClient, apiRequest } from '@/shared/api/client';
import { env } from '@/shared/config/env';
import { ENDPOINTS } from '@/shared/api/endpoints';
import type { Conversation, Message, MessageType } from '../model/types';

type ApiMessageDto = {
  id: string;
  matchId: string;
  senderId: string;
  content: string | null;
  tipo: string;
  ctaData: Record<string, unknown> | null;
  fileUrl: string | null;
  fileName?: string | null;
  fileType?: string | null;
  status: string;
  readAt: string | null;
  createdAt: string;
  isDeleted?: boolean;
  deletedAt?: string | null;
};

type ApiConversationDto = {
  matchId: string;
  eventoId: string;
  score: number;
  altoPotencial: boolean;
  otherUser: {
    id: string;
    nome: string | null;
    avatarUrl: string | null;
    cargo: string | null;
    empresa: string | null;
  };
  lastMessage: ApiMessageDto | null;
  unreadCount: number;
  updatedAt: string;
};

type UploadResponse = {
  fileUrl: string;
  fileName: string;
  fileType: string;
  size: number;
};

// fileUrl da API vem como caminho relativo (ex: /uploads/chat/uuid.png).
// Para renderizar no <img src> precisamos do host completo da API.
const apiHost = (() => {
  const url = new URL(env.VITE_API_URL);
  return `${url.protocol}//${url.host}`;
})();

export const absoluteFileUrl = (fileUrl: string | null | undefined): string | null => {
  if (!fileUrl) return null;
  if (fileUrl.startsWith('http://') || fileUrl.startsWith('https://')) return fileUrl;
  return `${apiHost}${fileUrl}`;
};

const fromMessageDto = (dto: ApiMessageDto): Message => ({
  id: dto.id,
  matchId: dto.matchId,
  authorId: dto.senderId,
  type: (dto.tipo as MessageType) ?? 'TEXT',
  content: dto.content ?? '',
  read: dto.status === 'LIDO',
  createdAt: dto.createdAt,
  fileUrl: dto.fileUrl ?? null,
  fileName: dto.fileName ?? null,
  fileType: dto.fileType ?? null,
  isDeleted: dto.isDeleted ?? false,
  deletedAt: dto.deletedAt ?? null,
});

const fromConversationDto = (dto: ApiConversationDto): Conversation => ({
  matchId: dto.matchId,
  participanteId: dto.otherUser.id,
  participanteNome: dto.otherUser.nome ?? 'Participante',
  participanteFotoUrl: dto.otherUser.avatarUrl,
  ultimaMensagem:
    dto.lastMessage?.content ?? (dto.lastMessage?.fileUrl ? '📎 Arquivo' : null),
  ultimaMensagemEm: dto.lastMessage?.createdAt ?? dto.updatedAt,
  naoLidas: dto.unreadCount,
});

export const chatApi = {
  listConversations: async (): Promise<{ conversations: Conversation[] }> => {
    const out = await apiRequest<{ items: ApiConversationDto[] }>({
      method: 'GET',
      url: ENDPOINTS.CHAT.CONVERSATIONS,
    });
    return { conversations: out.items.map(fromConversationDto) };
  },
  listMessages: async (matchId: string): Promise<{ messages: Message[] }> => {
    const out = await apiRequest<{ items: ApiMessageDto[] }>({
      method: 'GET',
      url: ENDPOINTS.CHAT.MESSAGES_LIST(matchId),
    });
    return { messages: out.items.map(fromMessageDto) };
  },
  sendMessage: async (
    matchId: string,
    content: string,
    type: MessageType = 'TEXT',
  ): Promise<{ message: Message }> => {
    const out = await apiRequest<{ message: ApiMessageDto; isDuplicate: boolean }>({
      method: 'POST',
      url: ENDPOINTS.CHAT.MESSAGES_SEND,
      data: { matchId, tipo: type, content },
    });
    return { message: fromMessageDto(out.message) };
  },
  sendFileMessage: async (
    matchId: string,
    file: { fileUrl: string; fileName: string; fileType: string },
    caption?: string,
  ): Promise<{ message: Message }> => {
    const out = await apiRequest<{ message: ApiMessageDto; isDuplicate: boolean }>({
      method: 'POST',
      url: ENDPOINTS.CHAT.MESSAGES_SEND,
      data: {
        matchId,
        tipo: 'FILE',
        content: caption ?? '',
        fileUrl: file.fileUrl,
        fileName: file.fileName,
        fileType: file.fileType,
      },
    });
    return { message: fromMessageDto(out.message) };
  },
  uploadFile: async (file: File): Promise<UploadResponse> => {
    const form = new FormData();
    form.append('file', file);
    // Bypassa apiRequest porque envelope eh diferente em multipart; usa
    // apiClient direto e desempacota o envelope manualmente.
    const response = await apiClient.request<{
      success: boolean;
      data: UploadResponse;
      error?: { code: string; message: string };
    }>({
      method: 'POST',
      url: ENDPOINTS.CHAT.UPLOAD,
      data: form,
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    if (!response.data.success) {
      throw new Error(response.data.error?.message ?? 'Falha no upload');
    }
    return response.data.data;
  },
  markRead: async (matchId: string): Promise<void> => {
    await apiRequest<{ marked: number }>({
      method: 'PATCH',
      url: ENDPOINTS.CHAT.READ(matchId),
    });
  },
  deleteMessage: async (messageId: string): Promise<{ message: Message }> => {
    const out = await apiRequest<{ message: ApiMessageDto }>({
      method: 'DELETE',
      url: ENDPOINTS.CHAT.MESSAGE_DELETE(messageId),
    });
    return { message: fromMessageDto(out.message) };
  },
};
